/* eslint-disable react/prop-types */
import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Notify } from "notiflix/build/notiflix-notify-aio";
import { Confirm } from "notiflix/build/notiflix-confirm-aio";
import { styled } from "@mui/material/styles";
import { Loading } from "notiflix/build/notiflix-loading-aio";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import IconButton from "@mui/material/IconButton";
import GridContainer from "components/Grid/GridContainer.jsx";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import Dialog from "@mui/material/Dialog";
import Tooltip from "@mui/material/Tooltip";
import RestoreFromTrashRoundedIcon from "@mui/icons-material/RestoreFromTrashRounded";
import EditTemplate from "./EditTemplate";
import Alert from "components/CustomAlert";
import { tables } from "utils/constants";
import { globalCountry } from "store/entities/countries";
import {
  useGetAllDefaultEmailTemplatesQuery,
  useGetAllEmailTemplatesByCountryQuery,
  useDeleteEmailTemplateMutation,
} from "services/email_templates";

function EmailTemplates() {
  const selectedCountry = useSelector(globalCountry);

  const { data: emailTemplates = [], isLoading: isEmailTemplatesLoading } =
    useGetAllEmailTemplatesByCountryQuery(selectedCountry.id);

  const { data: defaultEmailTemplates = [], isLoading: isDefaultEmailTemplatesLoading } =
    useGetAllDefaultEmailTemplatesQuery();

  const [deleteEmailTemplate, result] = useDeleteEmailTemplateMutation();

  const tableData = useMemo(() => {
    if (defaultEmailTemplates.length > 0) {
      if (emailTemplates.length > 0) {
        let data = defaultEmailTemplates.map((prop) => {
          let relatedEmailTemplate = emailTemplates.filter((item) => item.type == prop.type);
          if (relatedEmailTemplate.length > 0) {
            return {
              body: relatedEmailTemplate[0].body,
              country_id: relatedEmailTemplate[0].country_id,
              id: relatedEmailTemplate[0].id,
              subject: relatedEmailTemplate[0].subject,
              type: relatedEmailTemplate[0].type,
              recipients: relatedEmailTemplate[0].email_recipients,
            };
          } else {
            return {
              body: prop.body,
              country_id: prop.country_id,
              id: prop.id,
              subject: prop.subject,
              type: prop.type,
            };
          }
        });
        return data;
      } else {
        return defaultEmailTemplates;
      }
    } else {
      return [];
    }
  }, [defaultEmailTemplates, emailTemplates]);

  useEffect(() => {
    isEmailTemplatesLoading || isDefaultEmailTemplatesLoading
      ? Loading.dots({ svgColor: "rgba(159, 3, 3, 0.8)" })
      : Loading.remove(500);
  }, [isEmailTemplatesLoading, isDefaultEmailTemplatesLoading]);

  const deleteRecord = async (id) => {
    Loading.dots({
      svgColor: "rgba(159, 3, 3, 0.8)",
    });
    try {
      deleteEmailTemplate(id)
        .then(() => {
          Notify.success("Succefully Reset!");
        })
        .catch((error) => console.log(error));
    } catch (error) {
      console.log(error);
    } finally {
      Loading.remove(500);
    }
  };

  return (
    <Card>
      <CardHeader title={<MDTypography color="info">Email Templates</MDTypography>} />
      <CardContent>
        <br />
        <GridContainer></GridContainer>
        {tableData.length > 0 ? (
          <DataTable
            id={tables.EMAIL_TEMPLATES}
            columns={[
              { Header: "Type", accessor: "type", width: "45%", align: "left" },
              { Header: "Subject", accessor: "subject", align: "left" },
              {
                Header: "action",
                accessor: "action",
                align: "right",
                disableSortBy: true,
                disableFilters: true,
                show: true,
                Cell: ({ row: { original } }) => (
                  <div className="actions-right">
                    <EditTemplate initData={original} selectedCountryId={selectedCountry.id} />
                    {original.country_id != 0 ? (
                      <Tooltip title="Reset to default" sx={{ ml: 1 }} placement="top">
                        <IconButton
                          aria-label="view"
                          size="small"
                          onClick={() => {
                            Confirm.show(
                              `Please Confirm`,
                              "Are you sure you want to delete this template?",
                              "Yes",
                              "No",
                              () => {
                                deleteRecord(original.id);
                              },
                              () => {},
                              {}
                            );
                          }}
                        >
                          <RestoreFromTrashRoundedIcon color="info" fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      ""
                    )}
                  </div>
                ),
              },
            ]}
            data={tableData}
            isSorted={true}
            entriesPerPage={false}
            showTotalEntries={false}
            hideColumnSelection={false}
            noEndBorder
            canSearch
          />
        ) : (
          <Alert severity="warning">No templates found!</Alert>
        )}
      </CardContent>
    </Card>
  );
}

export default EmailTemplates;
